/**
 * MB-A BriefingView
 *
 * Rendert einen Briefing-Body (Markdown) mit react-markdown + remark-gfm +
 * rehype-sanitize. Externe Links erhalten target="_blank" rel="noopener noreferrer".
 *
 * Edit-Modus (nur wenn canBriefingManage): Textarea (raw Markdown) + Live-Preview
 * nebeneinander. Save → PATCH /v1/briefings/:bid. Cancel → revert.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { BriefingExportActions, buildBriefingFileName } from './briefing-export-actions.js';

// ---------------------------------------------------------------------------
// Markdown renderer — reused for both read and preview mode
// ---------------------------------------------------------------------------

const markdownComponents: Components = {
  a: ({ href, children, ...props }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: 'var(--primary)', textDecoration: 'underline' }}
      {...props}
    >
      {children}
    </a>
  ),
  h1: ({ children, ...props }) => (
    <h1 className="text-base font-semibold mt-4 mb-1" style={{ color: 'var(--ink)' }} {...props}>
      {children}
    </h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 className="text-sm font-semibold mt-3 mb-1" style={{ color: 'var(--ink)' }} {...props}>
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 className="text-sm font-medium mt-2 mb-0.5" style={{ color: 'var(--ink)' }} {...props}>
      {children}
    </h3>
  ),
  p: ({ children, ...props }) => (
    <p className="text-sm mb-2 leading-relaxed" style={{ color: 'var(--ink)' }} {...props}>
      {children}
    </p>
  ),
  // Listen — `list-outside` + eigenes Padding, NICHT `list-inside`.
  //
  // Hintergrund (Vorfall 2026-08-19, Briefing zu P022205): Mit `list-inside` sitzt
  // der Aufzählungspunkt IM Textfluss des <li>. Das geht nur gut, solange der
  // Eintrag reiner Inline-Text ist. Zwei Fälle brechen es:
  //
  //  1. „Lose" Listen (Markdown mit Leerzeile zwischen den Einträgen): Dann wickelt
  //     der Parser jeden Eintragsinhalt in ein <p>. Ein <p> ist ein Block — er
  //     bricht direkt hinter dem Marker um, der Punkt steht allein in seiner Zeile
  //     und der Text darunter. Genau das war als „verlorene Aufzählungszeichen"
  //     und „unnötige Zeilenumbrüche" sichtbar.
  //  2. Verschachtelte Listen: `list-inside` bringt kein padding-left mit, die
  //     Unterpunkte stehen also bündig unter den Hauptpunkten — die Hierarchie
  //     ist optisch weg.
  //
  // `list-outside` + `pl-5` hängt den Marker links neben den Textblock. Das
  // funktioniert für beide Fälle, weil der Marker den Textfluss gar nicht erst
  // betritt. Die Zusatzregeln räumen die Abstände auf, die dadurch sonst doppelt
  // wirken: <p> im Listeneintrag ohne Untenabstand, verschachtelte Liste ohne
  // eigenen Blockabstand.
  ul: ({ children, ...props }) => (
    <ul
      className="text-sm list-disc list-outside pl-5 mb-2 space-y-0.5 [&_li>p]:mb-0 [&_ul]:mb-0 [&_ul]:mt-0.5 [&_ol]:mb-0 [&_ol]:mt-0.5"
      style={{ color: 'var(--ink)' }}
      {...props}
    >
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol
      className="text-sm list-decimal list-outside pl-5 mb-2 space-y-0.5 [&_li>p]:mb-0 [&_ul]:mb-0 [&_ul]:mt-0.5 [&_ol]:mb-0 [&_ol]:mt-0.5"
      style={{ color: 'var(--ink)' }}
      {...props}
    >
      {children}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li className="leading-relaxed" {...props}>
      {children}
    </li>
  ),
  code: ({ children, className, ...props }) => {
    const isBlock = !!className;
    if (isBlock) {
      return (
        <pre
          className="text-xs p-3 rounded-md overflow-x-auto my-2"
          style={{
            background: 'var(--surface-2)',
            color: 'var(--ink)',
            fontFamily: 'var(--f-mono)',
          }}
        >
          <code {...props}>{children}</code>
        </pre>
      );
    }
    return (
      <code
        className="text-xs px-1 py-0.5 rounded"
        style={{ background: 'var(--surface-2)', color: 'var(--ink)', fontFamily: 'var(--f-mono)' }}
        {...props}
      >
        {children}
      </code>
    );
  },
  blockquote: ({ children, ...props }) => (
    <blockquote
      className="pl-3 my-2 text-sm italic"
      style={{ borderLeft: '3px solid var(--line-soft)', color: 'var(--ink-2)' }}
      {...props}
    >
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-3" style={{ borderColor: 'var(--line-soft)' }} />,
  strong: ({ children, ...props }) => (
    <strong className="font-semibold" style={{ color: 'var(--ink)' }} {...props}>
      {children}
    </strong>
  ),
  em: ({ children, ...props }) => (
    <em style={{ color: 'var(--ink-2)' }} {...props}>
      {children}
    </em>
  ),
  table: ({ children, ...props }) => (
    <div className="overflow-x-auto my-2">
      <table
        className="text-xs w-full border-collapse"
        style={{ borderColor: 'var(--line-soft)' }}
        {...props}
      >
        {children}
      </table>
    </div>
  ),
  th: ({ children, ...props }) => (
    <th
      className="px-2 py-1 text-left text-[10px] uppercase tracking-[0.1em]"
      style={{
        background: 'var(--surface-2)',
        color: 'var(--ink-3)',
        fontFamily: 'var(--f-mono)',
        borderBottom: '1px solid var(--line-soft)',
      }}
      {...props}
    >
      {children}
    </th>
  ),
  td: ({ children, ...props }) => (
    <td
      className="px-2 py-1 border-b"
      style={{ borderColor: 'var(--line-soft)', color: 'var(--ink)' }}
      {...props}
    >
      {children}
    </td>
  ),
};

// Entpackt einen umschließenden ```markdown … ```-Block (manche Modelle wickeln
// die ganze Antwort hinein → würde sonst als ein <pre> roh dargestellt). Spiegelt
// stripWrappingCodeFence im Backend; deckt auch bereits gespeicherte Altbestände
// ab, die noch mit Fence in der DB liegen.
function stripWrappingCodeFence(raw: string): string {
  const text = raw.trim();
  if (!text.startsWith('```') || !text.endsWith('```')) return raw;
  const firstNl = text.indexOf('\n');
  if (firstNl === -1) return raw;
  const info = text.slice(3, firstNl).trim().toLowerCase();
  if (info !== '' && info !== 'markdown' && info !== 'md') return raw;
  return text.slice(firstNl + 1, text.length - 3).trim();
}

export function MarkdownRenderer({ body }: { body: string }) {
  return (
    <div className="min-w-0 max-w-full break-words">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={markdownComponents}
      >
        {stripWrappingCodeFence(body)}
      </ReactMarkdown>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BriefingView — main export
// ---------------------------------------------------------------------------

export type BriefingViewProps = {
  briefingId: string;
  body: string;
  canBriefingManage: boolean;
  isSaving: boolean;
  onSave: (newBody: string) => void;
  projectKey: string;
  customerName: string;
};

export function BriefingView({
  briefingId: _briefingId,
  body,
  canBriefingManage,
  isSaving,
  onSave,
  projectKey,
  customerName,
}: BriefingViewProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(body);

  function handleEdit() {
    setDraft(body);
    setEditing(true);
  }

  function handleCancel() {
    setDraft(body);
    setEditing(false);
  }

  function handleSave() {
    if (!draft.trim() || draft === body) return;
    onSave(draft);
    // setEditing(false) is called by parent on success via re-render (body changes)
    // We close immediately and let parent re-render.
    setEditing(false);
  }

  if (editing) {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Raw editor */}
          <div>
            <div
              className="text-[10px] uppercase tracking-[0.12em] mb-1"
              style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
            >
              {t('v2.briefing_view.editor_label', 'Markdown')}
            </div>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={Math.min(40, Math.max(12, draft.split('\n').length + 2))}
              className="w-full text-sm px-3 py-2 rounded-md outline-none resize-y"
              style={{
                background: 'var(--surface)',
                color: 'var(--ink)',
                border: '1px solid var(--line-soft)',
                fontFamily: 'var(--f-mono)',
                whiteSpace: 'pre-wrap',
              }}
              aria-label={t('v2.briefing_view.editor_aria', 'Briefing-Markdown bearbeiten')}
            />
          </div>
          {/* Live preview */}
          <div>
            <div
              className="text-[10px] uppercase tracking-[0.12em] mb-1"
              style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
            >
              {t('v2.briefing_view.preview_label', 'Vorschau')}
            </div>
            <div
              className="px-3 py-2 rounded-md overflow-auto"
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--line-soft)',
                minHeight: '8rem',
              }}
            >
              {draft.trim() ? (
                <MarkdownRenderer body={draft} />
              ) : (
                <span className="text-sm italic" style={{ color: 'var(--ink-3)' }}>
                  {t('v2.briefing_view.preview_empty', '(leer)')}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={handleCancel}
            className="text-xs px-3 py-1.5 rounded-md"
            style={{
              background: 'var(--surface-2)',
              color: 'var(--ink-2)',
              border: '1px solid var(--line-soft)',
              cursor: 'pointer',
            }}
          >
            {t('v2.modal.cancel', 'Abbrechen')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !draft.trim() || draft === body}
            className="text-xs px-3 py-1.5 rounded-md"
            style={{
              background: 'var(--primary)',
              color: 'var(--on-primary)',
              border: 'none',
              opacity: isSaving || !draft.trim() || draft === body ? 0.5 : 1,
              cursor: isSaving || !draft.trim() || draft === body ? 'not-allowed' : 'pointer',
            }}
          >
            {isSaving ? t('common.saving', 'Speichert…') : t('common.save', 'Speichern')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0 max-w-full">
      <div className="flex items-center justify-between gap-1 mb-2">
        <BriefingExportActions
          text={stripWrappingCodeFence(body)}
          fileName={buildBriefingFileName(projectKey, customerName)}
        />
        {canBriefingManage && (
          <button
            type="button"
            onClick={handleEdit}
            className="text-xs px-2 py-1 rounded"
            style={{
              color: 'var(--primary)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--f-mono)',
            }}
          >
            ✎ {t('v2.detail.briefings_edit', 'Bearbeiten')}
          </button>
        )}
      </div>
      <MarkdownRenderer body={body} />
    </div>
  );
}
